import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ptcImage from '../../../imagenes/ptc.jpg';
import { safe } from '../../documentos/commercialExcelUtils';
import { PK_DECLA_COMPANY_LINES, PK_DECLA_TITLE } from './pkDeclaStaticContent';
import { drawPkDeclaFooterPdf } from './pkDeclaFooter';
import { pkDeclaExcelFileName } from './generatePkDeclaExcel';
import {
  PK_DECLA_GAP_AFTER_MERCH_MM,
  PK_DECLA_GAP_AFTER_TABLE_MM,
  pkDeclaContainerList,
} from './pkDeclaLayout';
import { drawPkDeclaStaticBlockPdf } from './pkDeclaStaticPdf';

const MARGIN = 14;
const PAGE_W = 210;
const CONTENT_W = PAGE_W - MARGIN * 2;
const LINE_H = 4.2;
const LINE_H_MERCH = 3.2;
const FONT_SIZE = 8;
const LABEL_VESSEL = 'Vessel Name';
const LABEL_CONSIGNEE = 'Consigned identifier';

export function pkDeclaPdfFileName(params) {
  return pkDeclaExcelFileName(params).replace(/\.xlsx$/i, '.pdf');
}

export function buildPkDeclaPdfDoc(params) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT_SIZE);

  let y = MARGIN;
  const imgProps = doc.getImageProperties(ptcImage);
  const logoW = 32;
  const logoH = (imgProps.height * logoW) / imgProps.width;
  doc.addImage(ptcImage, 'JPEG', MARGIN, y, logoW, logoH);
  y += logoH + 3;

  PK_DECLA_COMPANY_LINES.forEach((line, i) => {
    doc.setFont('helvetica', i === 0 ? 'bold' : 'normal');
    doc.text(line, MARGIN, y);
    y += LINE_H;
  });
  doc.setFont('helvetica', 'normal');
  y += 3;
  doc.setFont('helvetica', 'bold');
  doc.text(PK_DECLA_TITLE, PAGE_W / 2, y, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  y += LINE_H * 1.6;

  const centerX = PAGE_W / 2;
  const centerMaxW = CONTENT_W * 0.72;
  const lineStep = LINE_H * 0.85;

  const writeLabeledRow = (label, value, alwaysShowLabel = false) => {
    if (!alwaysShowLabel && !safe(value).trim()) return;
    doc.text(label, MARGIN, y);
    const text = safe(value).trim();
    if (text) {
      const wrapped = doc.splitTextToSize(text, centerMaxW);
      wrapped.forEach((ln, i) => {
        doc.text(ln, centerX, y + i * lineStep, { align: 'center' });
      });
      y += Math.max(lineStep, wrapped.length * lineStep);
    } else {
      y += lineStep;
    }
  };

  writeLabeledRow(LABEL_VESSEL, params.vesselName, true);
  writeLabeledRow(LABEL_CONSIGNEE, params.consigneeName);

  for (const line of params.consigneeAddressLines || []) {
    if (!safe(line).trim()) continue;
    const wrapped = doc.splitTextToSize(safe(line), centerMaxW);
    wrapped.forEach((ln) => {
      doc.text(ln, centerX, y, { align: 'center' });
      y += lineStep;
    });
  }

  y += LINE_H * 0.4;

  const bl = safe(params.blNumber);
  const inv = safe(params.numeroInvoice);
  const containerList = pkDeclaContainerList(params.containers);
  const tableBody =
    containerList.length > 0
      ? containerList.map((cont) => [cont, bl, inv])
      : [['', bl, inv]];

  const tableWidth = 118;
  const tableMarginLeft = (PAGE_W - tableWidth) / 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMarginLeft, right: tableMarginLeft },
    tableWidth,
    head: [['CONTAINERS', 'BL', 'Invoice']],
    body: tableBody,
    theme: 'grid',
    styles: {
      fontSize: FONT_SIZE,
      cellPadding: 0.9,
      minCellHeight: 5,
      halign: 'center',
      valign: 'middle',
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
    },
    columnStyles: {
      0: { cellWidth: 46 },
      1: { cellWidth: 42 },
      2: { cellWidth: 30 },
    },
    showHead: 'everyPage',
  });

  y = (doc.lastAutoTable?.finalY ?? y) + PK_DECLA_GAP_AFTER_TABLE_MM;

  (params.merchandiseLines || []).forEach((line) => {
    const wrapped = doc.splitTextToSize(safe(line), CONTENT_W);
    wrapped.forEach((ln) => {
      doc.text(ln, MARGIN, y);
      y += LINE_H_MERCH;
    });
  });
  y += PK_DECLA_GAP_AFTER_MERCH_MM;

  y = drawPkDeclaStaticBlockPdf(doc, y);
  y += LINE_H * 0.5;
  drawPkDeclaFooterPdf(doc, y, params.issueDateIso);

  return doc;
}

export function generatePkDeclaPdf(params) {
  buildPkDeclaPdfDoc(params).save(pkDeclaPdfFileName(params));
}
